import { useNavigate, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { getImageUrl } from "../../utils/imageUtils";

export const HeroBanner = ({ theme, storeAppearance }) => {
  const navigate = useNavigate();
  const { subdomain } = useParams();
  const banners = storeAppearance?.banners?.filter(b => b.is_active) || [];

  const BannerContent = ({ banner }) => {
    const hasImage = banner.image_url && banner.image_url.trim() !== '';

    return (
      <div
        className="relative py-12 md:py-16 overflow-hidden min-h-[350px] md:min-h-[450px] flex items-center w-full"
        style={{
          ...(hasImage
            ? {
                backgroundImage: `url(${getImageUrl(banner.image_url)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }
            : {
                backgroundColor: theme.primary_color || '#1a365d'
              }
          )
        }}
      >
        {/* Dark overlay: only show when NO image is present (text-only on solid background) */}
        {!hasImage && (
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/20" />
        )}
        <div className="relative max-w-7xl mx-auto px-4 w-full">
          <div className="max-w-xl text-left">
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
              style={{ textShadow: hasImage ? '0 2px 8px rgba(0,0,0,0.3)' : 'none' }}
            >
              {banner.title}
            </h2>
            <p
              className="text-lg md:text-xl text-white/90 mb-8 max-w-lg"
              style={{ textShadow: hasImage ? '0 1px 4px rgba(0,0,0,0.3)' : 'none' }}
            >
              {banner.description}
            </p>
            {banner.cta_text && banner.cta_text.trim() !== "" && (
              <Button
                size="lg"
                className="px-8 py-6 text-lg font-bold rounded-full transition-all hover:scale-105 shadow-xl"
                style={{ backgroundColor: theme.accent_color || theme.primary_color }}
                onClick={() => {
                  const link = banner.link;
                  if (link.startsWith("http")) {
                    window.location.href = link;
                  } else if (link.startsWith("#") && !link.includes("/")) {
                    document.querySelector(link)?.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    navigate(`/store/${subdomain}/${link.replace(/^\//, '')}`);
                  }
                }}
              >
                {banner.cta_text}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (banners.length > 0) {
    return (
      <section className="relative w-full">
        <Carousel
          opts={{ loop: true }}
          plugins={[Autoplay({ delay: 5000 })]}
          className="w-full"
        >
          <CarouselContent className="-ml-0">
            {banners.map((banner, index) => (
              <CarouselItem key={index} className="pl-0 border-none">
                <BannerContent banner={banner} />
              </CarouselItem>
            ))}
          </CarouselContent>
          {banners.length > 1 && (
            <>
              <CarouselPrevious className="left-4 bg-white/20 border-white/30 text-white hover:bg-white/40" />
              <CarouselNext className="right-4 bg-white/20 border-white/30 text-white hover:bg-white/40" />
            </>
          )}
        </Carousel>
      </section>
    );
  }

  // Fallback to legacy single banner
  const hasLegacyImage = storeAppearance.hero_image_url && storeAppearance.hero_image_url.trim() !== '';

  return (
    <section
      className="relative py-12 md:py-16 overflow-hidden min-h-[320px] md:min-h-[400px] flex items-center"
      style={{
        ...(hasLegacyImage
          ? {
              backgroundImage: `url(${getImageUrl(storeAppearance.hero_image_url)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }
          : {
              backgroundColor: theme.primary_color || '#1a365d'
            }
        )
      }}
    >
      {/* Dark overlay: only show when NO image is present */}
      {!hasLegacyImage && (
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/20" />
      )}
      <div className="relative max-w-7xl mx-auto px-4 w-full">
        <div className="max-w-xl">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4"
            style={{ textShadow: hasLegacyImage ? '0 2px 8px rgba(0,0,0,0.3)' : 'none' }}
          >
            {storeAppearance.hero_headline}
          </h2>
          <p
            className="text-lg text-white/90 mb-6"
            style={{ textShadow: hasLegacyImage ? '0 1px 4px rgba(0,0,0,0.3)' : 'none' }}
          >
            {storeAppearance.hero_subheadline}
          </p>
          {storeAppearance.hero_cta_text && storeAppearance.hero_cta_text.trim() !== "" && (
            <Button
              size="default"
              className="px-6 py-5 text-base font-semibold"
              style={{ backgroundColor: theme.accent_color }}
              onClick={() => {
                const link = storeAppearance.hero_cta_link || "#products";
                if (link.startsWith("http")) {
                  window.location.href = link;
                } else if (link.startsWith("#") && !link.includes("/")) {
                  document.querySelector(link)?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  navigate(`/store/${subdomain}/${link.replace(/^\//, '')}`);
                }
              }}
            >
              {storeAppearance.hero_cta_text}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
